import {
  WSActions,
  WSForm,
  WSInputText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";
import {
  contactCompanyValidator,
  contactNameValidator
} from "../../../../utils/validators";
import { IPayeeResponse } from "@wingspanhq/payments/dist/interfaces";
import { WSQueries } from "../../../../query/WSQuery";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryUserProfile } from "../../../../query/users/queries";
import { selectorIsEnterpriseUser } from "../../../../shared/selectors/selectorIsEnterpriseUser";
import { queriesPayees } from "@query";
import { selectorCounterpartyProfileWrite } from "../../selectors/selectorCounterpartyProfileWrite";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";

export interface EditContractorModalProps {
  payee: IPayeeResponse;

  onClose(result: boolean): void;
}

export const EditContractorModal: React.FC<EditContractorModalProps> = ({
  onClose,
  payee
}) => {
  const userId = useUserId();
  const queryUserProfile = useQueryUserProfile(userId);

  const xWingspanAccount = useXWingspanPayerId();
  const [updateProfile, updateProfileMeta] =
    queriesPayees.updatePayee.useMutation();

  return (
    <WSQueries
      queries={{
        queryUserProfile
      }}
    >
      {({ queryUserProfileData }) => {
        const isEnterpriseUser = selectorIsEnterpriseUser(queryUserProfileData);

        return (
          <WSForm
            defaultValues={{
              company: payee.payerOwnedData.payeeCompanyName,
              name: payee.payerOwnedData.payeeName,
              externalId: payee.payerOwnedData.payeeExternalId
            }}
            validationSchema={Yup.object().shape({
              company: contactCompanyValidator,
              name: contactNameValidator,
              externalId: Yup.string()
            })}
            onSubmit={async data => {
              // Name + company → v3 profile. External ID (enterprise-only) now
              // folds into the same v3 update — write it only when it changed.
              const externalIdChanged =
                isEnterpriseUser &&
                data.externalId !== payee.payerOwnedData.payeeExternalId;

              const result = await updateProfile({
                payeeId: payee.payeeId,
                payeeUpdate: {
                  profile: selectorCounterpartyProfileWrite({
                    name: data.name,
                    company: data.company
                  }),
                  ...(externalIdChanged ? { externalId: data.externalId } : {})
                },
                xWingspanAccount
              });

              if (!result) {
                return;
              }

              onClose(true);
            }}
          >
            <WSForm.Field
              mb="L"
              name="company"
              label="Contractor company"
              component={WSInputText}
              componentProps={{
                placeholder: "Contractor company"
              }}
            />

            <WSForm.Field
              mb="L"
              name="name"
              label="Contractor full name"
              component={WSInputText}
              componentProps={{
                placeholder: "Contractor full name"
              }}
            />

            {isEnterpriseUser && (
              <WSForm.Field
                mb="L"
                name="externalId"
                label="External ID"
                component={WSInputText}
              />
            )}

            <WSActions
              mt="3XL"
              alignment="fill"
              buttons={[
                {
                  label: "Update contractor",
                  kind: "Primary",
                  type: "submit",
                  loading: updateProfileMeta.isLoading
                },
                {
                  label: "Cancel",
                  kind: "Secondary",
                  type: "button",
                  onClick() {
                    onClose(false);
                  }
                }
              ]}
            />
          </WSForm>
        );
      }}
    </WSQueries>
  );
};
